// Agents IA group (AIAssistant, AIAssistantManagement, KnowledgeBoard,
// StatusInstructions) — formatting and the numbers derived from the API.
// Every derivation reads a field the backend returns (Backend app/api:
// ai_assistant.py, ai_runs.py, files.py, logistics.py); nothing is invented.
import { formatRelative as sharedRelative, formatShortDate, formatShortDateTime, parseUtc } from '../utils/dates';
import { getStatusClass } from '../constants/ticketStatuses';

export const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

// ---- formatting (French formats, Paris time) ---------------------------------

const nbsp = (text) => String(text).replace(/ /g, ' ');

export const formatCount = (value) => nbsp(Number(value || 0).toLocaleString('fr-FR'));

// 0.643 → « 64 % »; null → « — »
export const formatPercent = (ratio) => (ratio === null || ratio === undefined || Number.isNaN(Number(ratio))
  ? '—'
  : nbsp(`${Math.round(Number(ratio) * 100)} %`));

// Dates: the shared short format (utils/dates — parseUtc, Paris time).
// « 28 oct. » this year, « 28 oct. 2025 » before.
export const formatShortDay = (value, now) => formatShortDate(value, undefined, { now, empty: '' });

// « 28 oct., 14:05 »
export const formatStamp = (value, now) => formatShortDateTime(value, undefined, { now, empty: '' });

// « il y a 12 min », « il y a 3 h », « hier », « il y a 2 j », then the day.
export const formatRelative = (value, now) => sharedRelative(value, undefined, now, { empty: '' });

// ---- tools -------------------------------------------------------------------

export const toolFunctionName = (tool) => (tool && tool.function && tool.function.name) || (tool && tool.name) || '';

export const toolDescription = (tool) => {
  const description = tool && tool.function && tool.function.description;
  return typeof description === 'string' ? description.trim() : '';
};

// Readable names of the platform tools (existing tools.* keys).
export const toolLabel = (t, name) => {
  const labels = {
    retrieve_knowledge: t('tools.retrieveKnowledge', 'Utiliser les connaissances'),
    assign_to_employee: t('tools.assignToEmployee', 'Assigner à un agent'),
    resolve_ticket: t('tools.resolveTicket', 'Résoudre le ticket'),
    Close_ticket: t('tools.closeTicket', 'Fermer le ticket'),
    get_tracking_info: t('tools.getTrackingInfo', 'Récupérer les informations de suivi'),
    Retrieve_customer_order_by_reference_and_email: t('tools.retrieveOrder', 'Récupérer la commande'),
    Update_customer_shipping_address: t('tools.updateShippingAddress', 'Mettre à jour l’adresse de livraison'),
    find_relay_points: t('tools.findRelayPoints', 'Trouver les points relais'),
    create_return_with_edn_label: t('tools.createReturn', 'Créer un retour'),
    carriers_conversation: t('tools.carriersConversation', 'Conversation avec le transporteur'),
    Retour: t('tools.return', 'Retour'),
    fill_form_fields: t('tools.fillPDF', 'Remplir un PDF'),
    getOrderById: t('tools.getOrderById', 'Retrouver une commande'),
    carriers_conversation_update: t('tools.carriersConversationUpdate', 'Mettre à jour la conversation avec le transporteur'),
    order_status: t('tools.orderStatus', 'Statut de la commande'),
    Request_return_order: t('tools.Request_return_order', 'Demande de retour'),
    Retrieve_customer_order_by_ID: t('tools.Retrieve_customer_order_by_ID', 'Retrouver une commande'),
    Request_exchange_shopify: t('tools.Request_exchange_shopify', 'Demande d’échange'),
    Get_all_product_variants: t('tools.Get_all_product_variants', 'Retrouver variantes produit'),
    pass_response: t('tools.pass_response', 'Ne pas répondre'),
    change_ticket_status: t('tools.change_ticket_status', 'Changer le statut du ticket'),
  };
  return labels[name] || name;
};

// Grouped by what the tool does (the two carrier-contact sets first).
export const AGENT_CARRIER_TOOL_RE = /^(send_carrier_email|file_[a-z0-9_]+_request|file_fedex_claim)$/;
const ROUTER_CARRIER_TOOLS = ['contact_carrier', 'update_carrier_conversation'];
const TICKET_TOOLS = [
  'assign_to_employee', 'resolve_ticket', 'Close_ticket', 'change_ticket_status', 'pass_response',
  'attach_ticket_files', 'send_invoice', 'send_proof_of_delivery', 'retrieve_knowledge', 'send_slack_message',
  'warehouse_conversation_mail',
];
export const TOOL_GROUP_ORDER = ['agentCarrier', 'routerCarrier', 'ticket', 'tracking', 'orders', 'integrations', 'other', 'legacy'];

export const toolGroupOf = (tool) => {
  const name = toolFunctionName(tool);
  if (tool.deprecated_by) return 'legacy';
  if (AGENT_CARRIER_TOOL_RE.test(name)) return 'agentCarrier';
  if (ROUTER_CARRIER_TOOLS.includes(name)) return 'routerCarrier';
  if (['lead_form', 'pdf', 'openapi', 'connector'].includes(tool.source)) return 'integrations';
  if (TICKET_TOOLS.includes(name)) return 'ticket';
  if (/order|product|shopify|discount|return|exchange|sku|variant|stock|cart/i.test(name)) return 'orders';
  if (/tracking|proof|relay|delivery|parts|asus|shipment|compat|carrier|warehouse/i.test(name)) return 'tracking';
  return 'other';
};

export const toolGroupMeta = (t, group) => ({
  agentCarrier: {
    title: t('tools.groups.agentCarrier', 'Contact transporteur — l’agent choisit le canal'),
    hint: t('tools.groups.agentCarrierHint', 'Un outil par canal (e-mail) et par automatisation portail ou formulaire. Dès qu’un de ces outils est sélectionné, l’assistant décide lui-même du canal et de l’adresse à partir des fiches transporteurs et de vos règles ; les outils « canal décidé par la configuration » sont alors ignorés.'),
  },
  routerCarrier: {
    title: t('tools.groups.routerCarrier', 'Contact transporteur — canal décidé par la configuration'),
    hint: t('tools.groups.routerCarrierHint', 'Un seul outil : la configuration du transporteur (portail, formulaire, e-mail) choisit le canal et l’adresse à la place de l’assistant.'),
  },
  ticket: { title: t('tools.groups.ticket', 'Ticket et réponse'), hint: '' },
  tracking: { title: t('tools.groups.tracking', 'Suivi, transporteurs et entrepôt'), hint: '' },
  orders: { title: t('tools.groups.orders', 'Commandes et boutique'), hint: '' },
  integrations: { title: t('tools.groups.integrations', 'Vos intégrations (formulaires, PDF, API, connecteurs)'), hint: '' },
  other: { title: t('tools.groups.other', 'Autres outils'), hint: '' },
  legacy: {
    title: t('tools.groups.legacy', 'Anciens outils'),
    hint: t('tools.groups.legacyHint', 'Remplacés par les outils ci-dessus ; conservés pour les assistants qui les utilisent encore.'),
  },
}[group]);

// [{ group, tools }] in display order, each list sorted by function name.
export const groupTools = (tools) => {
  const grouped = {};
  (tools || []).forEach((tool) => {
    const group = toolGroupOf(tool);
    (grouped[group] = grouped[group] || []).push(tool);
  });
  return TOOL_GROUP_ORDER
    .filter((group) => grouped[group] && grouped[group].length)
    .map((group) => ({
      group,
      tools: grouped[group].slice().sort((a, b) => toolFunctionName(a).localeCompare(toolFunctionName(b))),
    }));
};

// The agent's selection against what /api/tools offers: ids the list no
// longer returns (tool removed, or its source not configured — KI-218) are
// never given to the agent.
export const agentTools = (agent, tools) => {
  const ids = (agent && agent.selected_tool_ids) || [];
  const byId = new Map((tools || []).map((tool) => [tool.id, tool]));
  const selected = [];
  const missing = [];
  ids.forEach((id) => {
    if (byId.has(id)) selected.push(byId.get(id));
    else missing.push(id);
  });
  return { selected, missing };
};

// ---- agents ------------------------------------------------------------------

// The channel keys of /api/users/available-channels (« Website », « WhatsApp »…).
// The test channel is never bound to an agent from the UI.
export const isTestChannel = (name) => String(name || '').toLowerCase() === 'test';

export const activeChannels = (agent) => Object.entries((agent && agent.associated_channels) || {})
  .filter(([name, on]) => on && !isTestChannel(name))
  .map(([name]) => name);

export const channelLabel = (t, name) => {
  const labels = {
    website: t('aiAgentsUi.channels.website', 'Chatbot du site'),
    whatsapp: 'WhatsApp',
    instagram: 'Instagram',
    messenger: 'Messenger',
    custom: t('aiAgentsUi.channels.custom', 'API tickets externes'),
    spacefill: 'Spacefill',
    test: t('aiAgentsUi.channels.test', 'Test'),
  };
  return labels[String(name || '').toLowerCase()] || name;
};

// The current version entry of an agent document (prompt_versions, last).
export const currentVersionEntry = (agent) => {
  const versions = (agent && agent.prompt_versions) || [];
  if (!versions.length) return null;
  const sorted = versions.slice().sort((a, b) => (a.version || 0) - (b.version || 0));
  return sorted[sorted.length - 1];
};

export const hasInstructions = (agent) => Boolean(agent && typeof agent.prompt === 'string' && agent.prompt.trim());

// « À vérifier » of the agents list: what a person must fix on an agent.
export const agentsAttention = (agents) => {
  const items = [];
  const noPrompt = (agents || []).filter((agent) => agent.prompt !== undefined && !hasInstructions(agent));
  noPrompt.forEach((agent) => items.push({ key: `noprompt-${agent._id}`, kind: 'noPrompt', tone: 'warning', agentId: agent._id, name: agent.name }));
  return items;
};

// ---- metrics of GET /api/ai-runs/assistants/{id}/versions ---------------------

const sumOf = (versions, read) => versions.reduce((acc, version) => acc + (Number(read(version.metrics || {})) || 0), 0);

const approvalOf = (versions, key, names) => {
  const out = {};
  names.forEach((name) => { out[name] = sumOf(versions, (m) => m.approval && m.approval[key] && m.approval[key][name]); });
  out.reviewed = names.reduce((acc, name) => acc + out[name], 0);
  out.rate = out.reviewed ? out.approved / out.reviewed : null;
  return out;
};

// One agent over the window: runs and outcomes of every version summed
// (feedback spans belong to one run, so the sums are exact). `tickets` is the
// current version's own count: a ticket worked by two versions would be
// counted twice by a sum.
export const summarizeVersions = (payload) => {
  if (!payload || !Array.isArray(payload.versions)) return null;
  const versions = payload.versions;
  let lastRunAt = null;
  versions.forEach((version) => {
    const last = parseUtc(version.metrics && version.metrics.last_run_at);
    if (last && (!lastRunAt || last > lastRunAt)) lastRunAt = last;
  });
  const currentHash = payload.assistant && payload.assistant.current_hash;
  const current = versions.find((version) => currentHash && version.prompt_hash === currentHash) || null;
  return {
    days: payload.days,
    runs: sumOf(versions, (m) => m.runs),
    failedRuns: sumOf(versions, (m) => m.failed_runs),
    lastRunAt: lastRunAt ? lastRunAt.toISOString() : null,
    drafts: approvalOf(versions, 'drafts', ['approved', 'edited', 'discarded', 'regenerated']),
    carrier: approvalOf(versions, 'carrier_messages', ['approved', 'edited', 'rejected']),
    statusOverrides: sumOf(versions, (m) => m.status_overrides),
    currentVersion: (payload.assistant && payload.assistant.current_version) || (current && current.version) || null,
    currentTickets: current && current.metrics ? current.metrics.tickets : null,
    versionCount: versions.filter((version) => version.version).length,
  };
};

// The whole account: the per-agent summaries added up.
export const summarizeAccount = (summaries) => {
  const list = (summaries || []).filter(Boolean);
  if (!list.length) return null;
  const add = (read) => list.reduce((acc, item) => acc + (Number(read(item)) || 0), 0);
  const drafts = { approved: add((s) => s.drafts.approved), edited: add((s) => s.drafts.edited), reviewed: add((s) => s.drafts.reviewed) };
  drafts.rate = drafts.reviewed ? drafts.approved / drafts.reviewed : null;
  const carrier = { approved: add((s) => s.carrier.approved), reviewed: add((s) => s.carrier.reviewed) };
  carrier.rate = carrier.reviewed ? carrier.approved / carrier.reviewed : null;
  return {
    runs: add((s) => s.runs),
    activeAgents: list.filter((s) => s.runs > 0).length,
    drafts,
    carrier,
    statusOverrides: add((s) => s.statusOverrides),
  };
};

// ---- knowledge (GET /api/files/list) -------------------------------------------

export const fileExtension = (resource) => {
  const source = (resource && (resource.name || resource.id)) || '';
  const match = source.match(/\.([a-z0-9]{1,5})$/i);
  return match ? match[1].toUpperCase() : null;
};

export const fileKind = (resource) => {
  const ext = fileExtension(resource);
  if (ext === 'PDF') return 'pdf';
  if (ext === 'TXT' || ext === 'MD') return 'text';
  return 'other';
};

// knowledge_index.status written by the re-indexing sweep (KI-185); absent
// until the sweep reached the file.
export const UNREADABLE_STATUSES = ['unreadable', 'no_text', 'source_missing'];
export const indexStatus = (resource) => {
  const state = resource && resource.knowledge_index;
  return state && state.status ? state.status : null;
};

export const knowledgeSummary = (files) => {
  const list = files || [];
  const summary = { total: list.length, pdf: 0, text: 0, other: 0, unreadable: 0, noSummary: 0, indexed: 0, pieces: 0 };
  list.forEach((file) => {
    summary[fileKind(file)] += 1;
    const status = indexStatus(file);
    if (UNREADABLE_STATUSES.includes(status)) summary.unreadable += 1;
    if (status === 'indexed' || status === 'current') {
      summary.indexed += 1;
      summary.pieces += Number(file.knowledge_index.pieces) || 0;
    }
    if (!String(file.description || '').trim()) summary.noSummary += 1;
  });
  return summary;
};

export const KNOWLEDGE_FILTERS = ['all', 'pdf', 'text', 'other', 'unreadable', 'nosummary'];
export const matchesKnowledgeFilter = (file, filter) => {
  switch (filter) {
    case 'pdf': case 'text': case 'other': return fileKind(file) === filter;
    case 'unreadable': return UNREADABLE_STATUSES.includes(indexStatus(file));
    case 'nosummary': return !String(file.description || '').trim();
    default: return true;
  }
};

// Sources per agent id, from the account-wide list.
export const countByAgent = (files) => {
  const counts = {};
  (files || []).forEach((file) => {
    const key = file.assistant_id || '';
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
};

// ---- status instructions (GET/PUT /api/logistics/status-instructions) --------

const FAMILY_OF_CLASS = {
  'status-new': 'open',
  'status-open': 'open',
  'status-processing': 'processing',
  'status-waiting': 'waiting',
  'status-pending': 'waiting',
  'status-automated': 'processing',
  'status-resolved': 'terminal',
  'status-closed': 'terminal',
};
export const STATUS_FAMILY_ORDER = ['open', 'waiting', 'processing', 'terminal', 'other'];
export const statusFamily = (value) => FAMILY_OF_CLASS[getStatusClass(value)] || 'other';

// The flags as the form holds them (instructions / aiDisabled / aiInactive by value).
export const statusFlags = (entry, form) => {
  const excluded = entry.ai_settable === true && Boolean(form.aiDisabled[entry.value]);
  return {
    custom: Boolean(String(form.instructions[entry.value] || '').trim()),
    excluded,
    inactive: entry.ai_inactive_locked === true || Boolean(form.aiInactive[entry.value]),
    // KI-315: the carriers the AI uses this status for (empty = every carrier).
    scope: entry.ai_settable === true && !excluded ? ((form.carrierScopes || {})[entry.value] || []) : [],
  };
};

// The carriers a status is restricted to, as sent to the API: every status the
// AI may set, an empty list meaning « every carrier » (it clears a scope).
export const carrierScopesPayload = (entries, scopes) => {
  const payload = {};
  (entries || []).filter((entry) => entry.ai_settable === true).forEach((entry) => {
    payload[entry.value] = [...((scopes || {})[entry.value] || [])];
  });
  return payload;
};

export const statusSummary = (entries, form) => {
  const summary = { total: 0, aiSettable: 0, custom: 0, excluded: 0, inactive: 0, inactiveLocked: 0 };
  (entries || []).forEach((entry) => {
    const flags = statusFlags(entry, form);
    summary.total += 1;
    if (entry.ai_settable === true) summary.aiSettable += 1;
    if (flags.custom) summary.custom += 1;
    if (flags.excluded) summary.excluded += 1;
    if (flags.inactive) summary.inactive += 1;
    if (entry.ai_inactive_locked === true) summary.inactiveLocked += 1;
  });
  return summary;
};

export const STATUS_FILTERS = ['all', 'custom', 'excluded', 'inactive'];
export const matchesStatusFilter = (entry, form, filter) => {
  if (filter === 'all') return true;
  return statusFlags(entry, form)[filter];
};

// The PUT body, unchanged since the page was built: every override (blank
// ones clear on the server), the AI-settable statuses excluded, and the
// statuses the AI leaves alone (the locked resolved ones are implied).
export const buildStatusPayload = (entries, form) => ({
  instructions: form.instructions,
  disabled_statuses: (entries || [])
    .filter((entry) => entry.ai_settable === true && form.aiDisabled[entry.value])
    .map((entry) => entry.value),
  inactive_statuses: (entries || [])
    .filter((entry) => entry.ai_inactive_locked !== true && form.aiInactive[entry.value])
    .map((entry) => entry.value),
  carrier_scopes: carrierScopesPayload(entries, form.carrierScopes),
});

export const statusFormFrom = (entries) => {
  const form = { instructions: {}, aiDisabled: {}, aiInactive: {}, carrierScopes: {} };
  (entries || []).forEach((entry) => {
    form.instructions[entry.value] = entry.custom_instruction || '';
    form.aiDisabled[entry.value] = entry.ai_disabled === true;
    form.aiInactive[entry.value] = entry.ai_inactive === true;
    form.carrierScopes[entry.value] = Array.isArray(entry.carrier_scope) ? entry.carrier_scope : [];
  });
  return form;
};

// ---- the instructions document -------------------------------------------------

// Agent prompts are structured text (« 2 TRANSPORTEURS », « 2.1 … », bullets,
// **bold**, `code`): parsed into blocks so the page renders the structure.
export const parsePromptBlocks = (raw) => {
  const blocks = [];
  (raw || '').split('\n').forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) { blocks.push({ type: 'space' }); return; }
    if (/^[─—\-_=]{4,}$/.test(trimmed)) { blocks.push({ type: 'hr' }); return; }
    if (/^<\/?[A-Z_]+>$/.test(trimmed)) { blocks.push({ type: 'tag', text: trimmed }); return; }
    const heading = trimmed.match(/^(\d+)[.)]?\s+([A-ZÀ-Ý][A-ZÀ-Ý0-9 &'’\-/()]+)$/);
    if (heading) { blocks.push({ type: 'heading', num: heading[1], text: heading[2] }); return; }
    const rule = trimmed.match(/^(\d+\.\d+(?:\s*bis)?)\s+(.*)$/);
    if (rule) { blocks.push({ type: 'rule', num: rule[1], text: rule[2] }); return; }
    if (/^[•*-]\s+/.test(trimmed)) { blocks.push({ type: 'bullet', text: trimmed.replace(/^[•*-]\s+/, '') }); return; }
    blocks.push({ type: 'p', text: trimmed });
  });
  // Collapse runs of blank lines into one spacer.
  return blocks.filter((block, index) => block.type !== 'space' || (index > 0 && blocks[index - 1].type !== 'space'));
};

export const wordCount = (text) => String(text || '').split(/\s+/).filter(Boolean).length;
